// libs.
import dayjs from 'dayjs';
import { h, VNode } from 'vue';

// constants.
import { HEALTH_STATUS_COLORS, HEALTH_CHECK_REOCCURRENCE } from '@/constants/hr';

/**
 * Constructs a full path to a document based on folder, version, and file name.
 *
 * @param folder - The folder where the document is stored.
 * @param version - The version of the document.
 * @param fileName - The name of the file.
 * @returns A string representing the full document path in the format `folder/version/fileName`.
 */
export function createDocumentPath(folder: string, version: string, fileName: string): string {
  return `${folder}/${version}/${fileName}`;
}

/**
 * Determines the MIMEType of an image based on its file extension.
 *
 * @param fileName - The name of the file (including extension).
 * @returns The corresponding MIME type as a string. Defaults to 'image/jpeg' if the extension is unknown.
 */
export function getImageFileMime(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';

  const mimeTypes: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    bmp: 'image/bmp',
    tif: 'image/tiff',
    tiff: 'image/tiff',
  };

  // default to 'image/jpeg' if extension is unknown
  return mimeTypes[ext] || 'image/jpeg';
}

/**
 * Renders an active status badge element with a background colour based on the value.
 *
 * @param {string} text - The status text to display inside the badge.
 * @param {string} value - The key used to determine the background colour from HEALTH_STATUS_COLORS.
 * @returns {VNode} A Vue VNode representing a div styled as an active status badge.
 */
export function renderContractStatusBadge(text: string, value: string): VNode {
  return h('div', {
    class: ['dt-status-badge'],
    style: { background: HEALTH_STATUS_COLORS[value] },
  }, text);
}

/**
 * Maps a health check recurrence value between its key and display form.
 *
 * Converts between internal recurrence keys (e.g., `"DAYS"`, `"WEEKS"`)
 * and their corresponding string representations (e.g., `"1_days"`, `"2_weeks"`),
 * depending on the input.
 *
 * @param value - The recurrence value to map (either key or mapped string).
 * @returns The mapped recurrence string or key, or `undefined` if no match is found.
 */
export function mapHealthCheckReoccurrence(value: string): string {
  // direct mapping (e.g. "DAYS" -> "1_days")
  if (HEALTH_CHECK_REOCCURRENCE[value]) {
    return HEALTH_CHECK_REOCCURRENCE[value];
  }

  // reverse mapping (e.g. "2_weeks" -> "WEEKS")
  const reverseEntry = Object.entries(HEALTH_CHECK_REOCCURRENCE).find(([_, val]) => val === value);
  const reverseKey = reverseEntry ? reverseEntry[0] : undefined;

  return reverseKey;
}

/**
 * Calculates the next check-up date based on start date, interval, and recurrence.
 *
 * @param startDate - the start date (string or Date)
 * @param interval - number of intervals (e.g. 1, 2, 3)
 * @param recurrence - recurrence string (e.g. '1_days', '2_weeks', '3_months', '4_years')
 * @returns new Date or null
 */
export function calculateNextCheckUpDate(startDate: string | Date, interval: number, recurrence: string): string | null {
  if (!startDate || !interval || !recurrence) {
    return null;
  }

  const date = dayjs(startDate);
  const normalized = recurrence.toLowerCase();

  let result = date;

  if (normalized.includes('day')) {
    result = result.add(interval, 'day');
  } else if (normalized.includes('week')) {
    result = result.add(interval, 'week');
  } else if (normalized.includes('month')) {
    result = result.add(interval, 'month');
  } else if (normalized.includes('year')) {
    result = result.add(interval, 'year');
  }

  return result.format('YYYY-MM-DD');
}
